import React from "react";
import { matchPath, useLocation } from "react-router-dom";
import { basePathOfTenant } from "shared/config/base-urls";
import { DeploymentEnvironment } from "shared/config/deployment-environments";
import { FrontendIframePageEnum, FrontendPageEnum } from "shared/model/pages";

import { GtmEventTabClick } from "../providers/gtm-events-provider/events";
import { useSendGtmEvents } from "../providers/gtm-events-provider/gtm-events-provider";
import { Link } from "~/components/link";
import { frontendPages } from "~/router/frontend-pages";
import { isIFramePage } from "~/router/is-page-type";
import { getRouteIcon } from "~/router/tenant-page/nav-links";
import { useGetPageInfo } from "~/router/use-get-page-info";
import { TenantPage, useTenantPages } from "~/router/use-tenant-pages";
import { cn } from "~/util/cn";
import { appEnv, isLocalhost } from "~/util/env-utils";

export const BottomNavigation: React.FC<{
  disabled: boolean;
  className?: string;
}> = ({ disabled, className }) => {
  const tenantPages = useTenantPages();

  const routes = tenantPages
    ?.filter((page) => {
      const isVisible =
        isIFramePage(page.page) || !frontendPages[page.page.route].hideInNavbar;
      const isDevPageInCorrectEnv = page.page.isDevPage
        ? appEnv === DeploymentEnvironment.DEV || isLocalhost
        : true;

      return isVisible && isDevPageInCorrectEnv;
    })
    .reduce(
      (acc, cur) => {
        const key = cur.page.route as keyof typeof acc;
        if (key in acc) acc[key].page = cur;

        return acc;
      },
      {
        [FrontendPageEnum.CHAT]: { target: "your_story" },
        [FrontendPageEnum.MAMA_ANSWER]: { target: "your_questions" },
        [FrontendPageEnum.REPORT]: { target: "summary" },
        [FrontendIframePageEnum.PATIENT_ASSOCIATION]: {},
      } as {
        [FrontendPageEnum.CHAT]: {
          page: TenantPage;
          target?: GtmEventTabClick["parameters"]["target"];
        };
        [FrontendPageEnum.MAMA_ANSWER]: {
          page: TenantPage;
          target?: GtmEventTabClick["parameters"]["target"];
        };
        [FrontendPageEnum.REPORT]: {
          page: TenantPage;
          target?: GtmEventTabClick["parameters"]["target"];
        };
        [FrontendIframePageEnum.PATIENT_ASSOCIATION]: {
          page: TenantPage;
          target?: GtmEventTabClick["parameters"]["target"];
        };
      },
    );

  const routesArray = Object.values(routes ?? {}).filter(
    (route) => !!route.page,
  );
  const routesArrayLength = routesArray.length;

  return (
    <menu
      className={cn(
        "z-10 grid place-items-center gap-1 border-x border-t border-x-neutral-200 border-b-neutral-200 border-t-neutral-100 bg-base-100",
        routesArrayLength === 3 ? "grid-cols-3" : "grid-cols-4",
        className,
      )}
    >
      {routesArray.map((route, index) =>
        route ? (
          <BottomNavigationButton
            key={`bottom-navigation-button-${index}`}
            target={route.target}
            disabled={disabled}
            tenantPage={route.page}
          />
        ) : null,
      )}
    </menu>
  );
};

const BottomNavigationButton: React.FC<{
  target?: GtmEventTabClick["parameters"]["target"];
  tenantPage: TenantPage;
  disabled: boolean;
}> = ({ target, tenantPage: { tenantId, page }, disabled }) => {
  const getPageInfo = useGetPageInfo();
  const { pathname } = useLocation();
  const { sendTabEvent } = useSendGtmEvents();

  const isActive = !!matchPath(
    pathname,
    basePathOfTenant(tenantId) + page.route,
  );

  return (
    <Link
      href={basePathOfTenant(tenantId) + page.route}
      disabled={disabled}
      className="flex flex-col items-center gap-1 text-xs font-medium"
      onClick={() =>
        target &&
        sendTabEvent({
          eventName: "tab_click",
          target,
          bottomNavigation: true,
        })
      }
    >
      <span
        className={cn(
          isActive ? "text-neutral-900" : "text-neutral-400",
          "transition-colors duration-500 ease-in-out",
        )}
      >
        {getRouteIcon(page.route)}
      </span>
      <span
        className={cn(
          isActive ? "text-neutral-900" : "text-neutral-500",
          "transition-colors duration-500 ease-in-out",
        )}
      >
        {
          getPageInfo({
            page,
            tenantId,
          }).title
        }
      </span>
    </Link>
  );
};
